/**
 * DailyActionBox Component
 * 
 * The critical training action section where users perform exactly ONE action per day:
 * - Approve Replica Draft
 * - Edit & Teach
 * - Reject & Explain
 * 
 * Each action requires specific feedback inputs for supervised learning.
 */

import React, { useState } from 'react';
import { Check, Edit2, X, MessageSquare } from 'lucide-react';
import type { TrainingAction, FeedbackReason } from '../../types/trainingTypes';

interface DailyActionBoxProps {
    replicaText: string;
    onApprove: () => void;
    onEditAndTeach: (editedText: string, reasons: FeedbackReason[], note?: string) => void;
    onRejectAndExplain: (reasons: FeedbackReason[], note?: string) => void;
    isSubmitting: boolean;
}

const FEEDBACK_REASONS: { value: FeedbackReason; label: string }[] = [
    { value: 'tone', label: 'Tone' },
    { value: 'missing_info', label: 'Missing Info' },
    { value: 'structure', label: 'Structure' },
    { value: 'policy', label: 'Policy' },
    { value: 'safety', label: 'Safety' },
    { value: 'other', label: 'Other' }
];

export const DailyActionBox: React.FC<DailyActionBoxProps> = ({
    replicaText,
    onApprove,
    onEditAndTeach,
    onRejectAndExplain,
    isSubmitting
}) => {
    const [selectedAction, setSelectedAction] = useState<TrainingAction | null>(null);
    const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
    const [feedbackNote, setFeedbackNote] = useState('');
    const [editedText, setEditedText] = useState(replicaText);

    const toggleReason = (reason: FeedbackReason) => {
        setSelectedReasons(prev =>
            prev.includes(reason)
                ? prev.filter(r => r !== reason)
                : [...prev, reason]
        );
    };

    const handleSubmit = () => {
        if (selectedAction === 'approve') {
            onApprove();
        } else if (selectedAction === 'edit_teach') {
            onEditAndTeach(editedText, selectedReasons, feedbackNote || undefined);
        } else if (selectedAction === 'reject_explain') {
            onRejectAndExplain(selectedReasons, feedbackNote || undefined);
        }
    };

    const canSubmit = () => {
        if (isSubmitting) return false;
        if (selectedAction === 'approve') return true;
        if (selectedAction === 'edit_teach') return selectedReasons.length > 0 && editedText !== replicaText;
        if (selectedAction === 'reject_explain') return selectedReasons.length > 0;
        return false;
    };

    return (
        <div className="card-elevated p-6 space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-lg font-semibold text-hig-text-primary">
                    Daily Training Action
                </h2>
                <p className="text-sm text-hig-text-tertiary mt-1">
                    Select one action to train your Replica
                </p>
            </div>

            {/* Action Selection */}
            <div className="grid grid-cols-3 gap-3">
                <ActionButton
                    selected={selectedAction === 'approve'}
                    onClick={() => {
                        setSelectedAction('approve');
                        setSelectedReasons([]);
                        setFeedbackNote('');
                    }}
                    icon={<Check className="w-5 h-5" />}
                    label="Approve"
                    description="Accept as-is"
                    variant="success"
                />
                <ActionButton
                    selected={selectedAction === 'edit_teach'}
                    onClick={() => {
                        setSelectedAction('edit_teach');
                        setEditedText(replicaText);
                    }}
                    icon={<Edit2 className="w-5 h-5" />}
                    label="Edit & Teach"
                    description="Correct output"
                    variant="warning"
                />
                <ActionButton
                    selected={selectedAction === 'reject_explain'}
                    onClick={() => {
                        setSelectedAction('reject_explain');
                        setSelectedReasons([]);
                        setFeedbackNote('');
                    }}
                    icon={<X className="w-5 h-5" />}
                    label="Reject"
                    description="Explain issue"
                    variant="error"
                />
            </div>

            {/* Approval Confirmation */}
            {selectedAction === 'approve' && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-400 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-green-400">
                                Approve Replica Output
                            </p>
                            <p className="text-xs text-green-400/70 mt-1">
                                This will mark the case as successful and store it as a validated example.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit & Teach Interface */}
            {selectedAction === 'edit_teach' && (
                <div className="space-y-4">
                    {/* Inline Editor */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-hig-text-secondary uppercase tracking-wide">
                            Edit Replica Output
                        </label>
                        <textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            className="input min-h-[200px] resize-y font-mono text-sm"
                        />
                        {editedText !== replicaText && (
                            <p className="text-xs text-hig-accent">
                                Changes detected — submit to train your Replica
                            </p>
                        )}
                    </div>

                    {/* Reason Chips */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-hig-text-secondary uppercase tracking-wide">
                            Why did you make these changes? (Select at least one)
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {FEEDBACK_REASONS.map(({ value, label }) => (
                                <ReasonChip
                                    key={value}
                                    selected={selectedReasons.includes(value)}
                                    onClick={() => toggleReason(value)}
                                    label={label}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Note for "Other" */}
                    {selectedReasons.includes('other') && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-hig-text-secondary uppercase tracking-wide flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                Explain "Other"
                            </label>
                            <input
                                type="text"
                                value={feedbackNote}
                                onChange={(e) => setFeedbackNote(e.target.value)}
                                className="input"
                                placeholder="Briefly describe the reason..."
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Reject & Explain Interface */}
            {selectedAction === 'reject_explain' && (
                <div className="space-y-4">
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-start gap-3">
                            <X className="w-5 h-5 text-red-400 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-red-400">
                                    Reject Replica Output
                                </p>
                                <p className="text-xs text-red-400/70 mt-1">
                                    The output will be marked as rejected. You must provide a reason.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Reason Chips */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-hig-text-secondary uppercase tracking-wide">
                            Why are you rejecting this? (Select at least one)
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {FEEDBACK_REASONS.map(({ value, label }) => (
                                <ReasonChip
                                    key={value}
                                    selected={selectedReasons.includes(value)}
                                    onClick={() => toggleReason(value)}
                                    label={label}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Optional Note */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-hig-text-secondary uppercase tracking-wide flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Additional Notes (Optional)
                        </label>
                        <input
                            type="text"
                            value={feedbackNote}
                            onChange={(e) => setFeedbackNote(e.target.value)}
                            className="input"
                            placeholder="Explain what was wrong..."
                        />
                    </div>
                </div>
            )}

            {/* Submit Button */}
            {selectedAction && (
                <button
                    onClick={handleSubmit}
                    disabled={!canSubmit()}
                    className={`
            w-full py-3 rounded-xl font-semibold transition-all
            ${selectedAction === 'approve'
                            ? 'bg-green-600 hover:bg-green-500 text-white'
                            : selectedAction === 'edit_teach'
                                ? 'bg-yellow-600 hover:bg-yellow-500 text-white'
                                : 'bg-red-600 hover:bg-red-500 text-white'
                        }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                        </span>
                    ) : (
                        <>
                            {selectedAction === 'approve' && 'Approve & Train'}
                            {selectedAction === 'edit_teach' && 'Submit Corrections'}
                            {selectedAction === 'reject_explain' && 'Reject & Explain'}
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

// ============================================
// ACTION BUTTON SUB-COMPONENT
// ============================================

interface ActionButtonProps {
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    description: string;
    variant: 'success' | 'warning' | 'error';
}

const ActionButton: React.FC<ActionButtonProps> = ({
    selected,
    onClick,
    icon,
    label,
    description,
    variant
}) => {
    const variantStyles = {
        success: {
            selected: 'border-green-500 bg-green-500/10',
            icon: 'bg-green-500/20 text-green-400',
            text: 'text-green-400'
        },
        warning: {
            selected: 'border-yellow-500 bg-yellow-500/10',
            icon: 'bg-yellow-500/20 text-yellow-400',
            text: 'text-yellow-400'
        },
        error: {
            selected: 'border-red-500 bg-red-500/10',
            icon: 'bg-red-500/20 text-red-400',
            text: 'text-red-400'
        }
    };

    const styles = variantStyles[variant];

    return (
        <button
            onClick={onClick}
            className={`
        p-4 rounded-xl border-2 transition-all text-center
        ${selected
                    ? styles.selected
                    : 'border-hig-border bg-hig-bg-secondary hover:border-hig-text-tertiary'
                }
      `}
        >
            <div className={`
        w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center
        ${selected ? styles.icon : 'bg-hig-bg-tertiary text-hig-text-tertiary'}
      `}>
                {icon}
            </div>
            <div className={`font-medium ${selected ? styles.text : 'text-hig-text-secondary'}`}>
                {label}
            </div>
            <div className="text-xs text-hig-text-tertiary mt-0.5">
                {description}
            </div>
        </button>
    );
};

// ============================================
// REASON CHIP SUB-COMPONENT
// ============================================

interface ReasonChipProps {
    selected: boolean;
    onClick: () => void;
    label: string;
}

const ReasonChip: React.FC<ReasonChipProps> = ({
    selected,
    onClick,
    label
}) => {
    return (
        <button
            onClick={onClick}
            className={`
        px-3 py-1.5 rounded-full text-sm font-medium transition-all
        ${selected
                    ? 'bg-hig-accent text-white'
                    : 'bg-hig-bg-tertiary text-hig-text-secondary border border-hig-border hover:border-hig-text-tertiary'
                }
      `}
        >
            {label}
        </button>
    );
};

export default DailyActionBox;
